// Helpers for the connections UI, kept out of the component so the two rules
// that are easy to get wrong can be tested.

// suggestSlug proposes a free name for a new connection. The provider's own
// name is the obvious first choice; the second one of the same provider has to
// differ, which is the entire reason connections carry a name at all.
export function suggestSlug(provider, existing) {
  const taken = new Set((existing || []).map((c) => c.slug));
  const base = provider.name;
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n++) {
    if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
  }
  return base;
}

// slugify derives the reference an app uses from the name a person typed, so
// nobody has to invent both. It produces only what the API accepts: lowercase
// letters, digits and dashes, at most 32 characters, never leading or trailing
// with a dash.
export function slugify(name) {
  return (name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // Apostrophes vanish rather than becoming a dash, so "Phil's key" reads as
    // phils-key and not phil-s-key.
    .replace(/['\u2019]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32)
    .replace(/-+$/g, "");
}

// filterSlugInput cleans the reference field as a person types, so an invalid
// character never lands in the box in the first place. Unlike slugify it does
// NOT collapse or trim dashes: doing that mid-word fights someone still typing
// "work-calendar". It only lowercases letters, drops anything that is not a
// letter, digit or dash, and caps the length the API allows.
// connectionLabel is the name to store: what the user typed, or the placeholder
// they saw (name_hint, then the provider label) when they left it blank. Picking
// a name is optional -- the hint is a fine default.
export function connectionLabel(typed, provider) {
  return (typed || "").trim() || provider.name_hint || provider.label || "";
}

export function filterSlugInput(value) {
  return (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 32);
}

// sameScopeKeys compares two read-scope selections as sets, so the edit dialog
// only reconnects when the granted permissions actually differ -- order and
// duplicates do not matter.
export function sameScopeKeys(a, b) {
  const sa = new Set(a || []);
  const sb = new Set(b || []);
  if (sa.size !== sb.size) return false;
  for (const key of sa) {
    if (!sb.has(key)) return false;
  }
  return true;
}

// iconURL is where an icon reference is served, for the one place (the admin
// page's picker) that names icons itself rather than reading icon_url.
export function iconURL(name) {
  return name ? `/api/icons/${name}` : "";
}

// filterTools narrows an MCP server's tool list as someone types. It matches the
// description as well as the name, because people remember what a tool DOES long
// before they remember whether it was called search_issues or issues_search.
export function filterTools(tools, query) {
  const list = tools || [];
  const q = (query || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (t) => (t.name || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q)
  );
}

// defaultScopeKeys are the read options checked when the connect dialog opens --
// every option the provider marks default. A provider with no options yields an
// empty list, so the checklist simply does not render.
export function defaultScopeKeys(provider) {
  return (provider?.scope_options || []).filter((o) => o.default).map((o) => o.key);
}

// suggestedToConnect lists what the welcome nudge offers: the providers and
// named MCP servers the operator marked suggested, minus what the person has
// connected already (or with those marked, when includeConnected is set). An
// MCP server counts as connected when a connection points at its URL.
export function suggestedToConnect(data, { includeConnected = false } = {}) {
  const conns = data?.connections || [];
  const byProvider = new Set(conns.map((c) => c.provider));
  const byURL = new Set(conns.filter((c) => c.kind === "mcp").map((c) => c.url));
  const out = [];
  for (const p of data?.providers || []) {
    if (p.suggested) out.push({ ...p, connected: byProvider.has(p.name) });
  }
  for (const s of data?.mcp_servers || []) {
    if (s.suggested) out.push({ ...s, kind: "mcp", connected: byURL.has(s.url) });
  }
  return includeConnected ? out : out.filter((s) => !s.connected);
}

// matchesFilter is the gallery's filter: a kind, "default" (granted to every
// new chat and app), "attention" (needs a reconnect), and a text search over
// the name, reference and provider.
export function matchesFilter(c, filter, query) {
  if (filter === "default" && !c.default_grant) return false;
  if (filter === "attention" && !["needs_reconnect", "needs_admin", "no_access", "unreachable"].includes(c.status)) return false;
  if (["oauth", "static", "mcp"].includes(filter) && c.kind !== filter) return false;
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return [c.label, c.slug, c.provider_label, c.provider].some((v) => (v || "").toLowerCase().includes(q));
}

// addableProviders is the provider list minus the ones the person already
// has and cannot add twice: the server keeps a suggested one listed (flagged
// connected) so the welcome tour can show it as done, but it is nothing to
// offer in a menu or a catalog.
export function addableProviders(providers) {
  return (providers || []).filter((p) => !p.connected);
}

// tourSteps is the welcome tour's page list: the level page always; the
// connections page whenever the operator suggested anything, connected or
// not (a returning person sees what is done); the operator's own page last,
// when control.yml names one.
export function tourSteps(offers, welcomeURL) {
  const steps = ["level"];
  if ((offers || []).length > 0) steps.push("connections");
  if (welcomeURL) steps.push("help");
  return steps;
}

// catalogTiles is what an empty gallery offers: every provider this instance
// can connect (the generic key last, MCP as its named servers plus "any URL").
export function catalogTiles(data) {
  const addable = addableProviders(data.providers);
  const providers = addable.filter((p) => p.kind !== "mcp" && p.name !== "generic");
  const generic = addable.filter((p) => p.name === "generic");
  const mcp = addable.find((p) => p.kind === "mcp");
  const servers = (data.mcp_servers || []).map((s) => ({ ...s, kind: "mcp" }));
  const any = mcp ? [{ name: "", label: "Any MCP server", url: "", icon_url: mcp.icon_url, kind: "mcp", help: "By URL" }] : [];
  return [...providers, ...generic, ...servers, ...any];
}
